import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, type BusinessType } from "@/stores/auth";
import { Building2, Coffee, Hotel, ShoppingBag, Flower2, Check, Music2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/onboarding")({
  head: () => ({ meta: [{ title: "Tell us about your space — Sura" }] }),
  component: Onboarding,
});

const iconFor = (name: string) => {
  const n = name.toLowerCase();
  if (n.includes("rest")) return Building2;
  if (n.includes("café") || n.includes("cafe") || n.includes("coffee")) return Coffee;
  if (n.includes("hotel")) return Hotel;
  if (n.includes("mall") || n.includes("retail") || n.includes("shop")) return ShoppingBag;
  if (n.includes("spa")) return Flower2;
  return Music2;
};

function Onboarding() {
  const navigate = useNavigate();
  const { user, init, initialized } = useAuth();
  const [types, setTypes] = useState<BusinessType[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!initialized) init(); }, [initialized, init]);

  useEffect(() => {
    api.get("/api/users/business-types").then(({ data }) => {
      setTypes(data.businessTypes || data.business_types || data || []);
    }).catch(() => toast.error("Could not load business types"));
  }, []);

  const toggle = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const submit = async () => {
    if (!selected.size) return toast.error("Select at least one");
    setLoading(true);
    try {
      await api.post("/api/users/onboarding", { business_type_ids: Array.from(selected) });
      toast.success("All set");
      // Refresh user data to get business types
      await useAuth.getState().init();
      navigate({ to: "/home" });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary/40 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            What kind of space{user ? `, ${user.full_name?.split(" ")[0]}` : ""}?
          </h1>
          <p className="mt-3 text-muted-foreground">Pick all that apply — we'll tailor your sound.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {types.map((t) => {
            const Icon = iconFor(t.name);
            const sel = selected.has(t.id);
            return (
              <button
                key={t.id}
                onClick={() => toggle(t.id)}
                className={`relative p-6 rounded-2xl border-2 bg-card text-left transition-all ${
                  sel ? "border-primary shadow-[var(--shadow-soft)]" : "border-border hover:border-primary/30"
                }`}
              >
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center mb-3 ${sel ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-semibold">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                {sel && (
                  <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <button
            onClick={submit}
            disabled={loading || !selected.size}
            className="px-8 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-60"
          >
            {loading ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
